import { useEffect, useRef, useState } from "react"

interface PillState {
  text: string
  top: number
  left: number
}

export default function AgentQuoteSelection({
  onQuote,
  children,
  disabled = false
}: {
  onQuote: (text: string) => void
  children: React.ReactNode
  disabled?: boolean
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [pill, setPill] = useState<PillState | null>(null)

  useEffect(() => {
    if (disabled && pill) setPill(null)
  }, [disabled, pill])

  const handleMouseUp = () => {
    if (disabled) {
      setPill(null)
      return
    }
    const sel = window.getSelection()
    const text = sel?.toString().trim() ?? ""
    if (!text || text.length < 2) {
      setPill(null)
      return
    }
    if (!sel?.rangeCount) {
      setPill(null)
      return
    }
    const range = sel.getRangeAt(0)
    // Check both the range ancestor AND the selection anchor/focus nodes.
    // After MarkdownRender re-renders, the range's commonAncestorContainer
    // may point to a detached DOM node even though the selection is visually
    // inside our container. Checking anchorNode covers that edge case.
    const container = containerRef.current
    if (
      !container ||
      (!container.contains(range.commonAncestorContainer) &&
        !container.contains(sel.anchorNode))
    ) {
      setPill(null)
      return
    }
    // Anchor the pill at the end of the selection (matches the
    // qna/briefing inline selection pattern). For multi-line selections
    // getClientRects() returns one rect per line — the last one is the
    // end of the visible selection. Fall back to the bounding rect if
    // getClientRects is empty (rare, e.g. zero-width selections).
    const rects = range.getClientRects()
    const endRect = rects.length ? rects[rects.length - 1] : null
    const fallback = range.getBoundingClientRect()
    const top = endRect ? endRect.top : fallback.top
    const left = endRect ? endRect.right : fallback.right
    setPill({ text, top, left })
  }

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (!(e.target as Element).closest?.("[data-agent-quote-ui]")) {
        // Defer to avoid calling setState from a native listener during
        // React's commit phase (React error #421)
        queueMicrotask(() => setPill(null))
      }
    }
    document.addEventListener("mousedown", handleMouseDown)
    return () => document.removeEventListener("mousedown", handleMouseDown)
  }, [])

  return (
    <div ref={containerRef} onMouseUp={handleMouseUp}>
      {children}
      {pill && !disabled && (
        <div
          data-agent-quote-ui="true"
          className="fixed z-[9998] flex items-center gap-1.5 px-2.5 py-1.5 bg-white dark:bg-dark-1 border border-light-border dark:border-border rounded-full shadow-md text-xs text-dark-2 dark:text-dark-6 cursor-pointer hover:bg-gray-50 dark:hover:bg-dark-2 transition-colors select-none"
          style={{
            top: Math.max(8, pill.top),
            left: Math.min(pill.left + 6, window.innerWidth - 160)
          }}
          onMouseDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            onQuote(pill.text)
            setPill(null)
            window.getSelection()?.removeAllRanges()
          }}
        >
          <svg
            viewBox="0 0 24 24"
            className="w-3.5 h-3.5 text-primary"
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M20.616 10.835a14.147 14.147 0 01-4.45-3.001 14.111 14.111 0 01-3.678-6.452.503.503 0 00-.975 0 14.134 14.134 0 01-3.679 6.452 14.155 14.155 0 01-4.45 3.001c-.65.28-1.318.505-2.002.678a.502.502 0 000 .975c.684.172 1.35.397 2.002.677a14.147 14.147 0 014.45 3.001 14.112 14.112 0 013.679 6.453.502.502 0 00.975 0c.172-.685.397-1.351.677-2.003a14.145 14.145 0 013.001-4.45 14.113 14.113 0 016.453-3.678.503.503 0 000-.975 13.245 13.245 0 01-2.003-.678z" />
          </svg>
          Ask OrbitShift
        </div>
      )}
    </div>
  )
}
